/* eslint-disable prefer-arrow-callback */
import { type MutableRefObject, useEffect, useRef } from "react";

import type { ScenarioScroll } from "pages/app/scenario-analysis/hooks/useScenarioScroll";

type UseChartScrollOnRecalcStartArgs = {
  scroll: ScenarioScroll;
  isCalculating: boolean;
  // Set by manual recalc triggers; consumed when the recalc starts, so the user lands on the chart before results arrive.
  scrollToChartOnStartRef: MutableRefObject<boolean>;
};

// Lands on the chart one frame after a manual recalc starts, once the chart section has painted.
export const useChartScrollOnRecalcStart = ({ scroll, isCalculating, scrollToChartOnStartRef }: UseChartScrollOnRecalcStartArgs) => {
  const { scrollToChart } = scroll;
  const wasCalculatingRef = useRef(false);
  // Only a recalc that scrolled at its start re-anchors when it settles.
  const reanchorOnSettleRef = useRef(false);

  useEffect(
    function scrollToChartOnCalculationStart() {
      const wasCalculating = wasCalculatingRef.current;
      wasCalculatingRef.current = isCalculating;

      const justStarted = !wasCalculating && isCalculating;
      if (justStarted && scrollToChartOnStartRef.current) {
        scrollToChartOnStartRef.current = false;
        reanchorOnSettleRef.current = true;
        const frame = requestAnimationFrame(function applyStartOfCalcChartScroll() {
          scrollToChart("smooth");
        });
        return function cancelStartOfCalcChartScroll() {
          cancelAnimationFrame(frame);
        };
      }

      // Taller results can push the chart back out of view, so re-anchor once the new layout commits.
      const justFinished = wasCalculating && !isCalculating;
      if (justFinished && reanchorOnSettleRef.current) {
        reanchorOnSettleRef.current = false;
        const frame = requestAnimationFrame(function applySettledChartScroll() {
          scrollToChart("smooth");
        });
        return function cancelSettledChartScroll() {
          cancelAnimationFrame(frame);
        };
      }

      return undefined;
    },
    [isCalculating, scrollToChartOnStartRef, scrollToChart]
  );
};
